// LEGACY SHIM — backed by ClientsContext + clientStore cache.
//
// New code should use the React hook `useClients()` from
// `src/context/ClientsContext.jsx`. This file remains so legacy
// synchronous callers (FinancialDataContext, TaxSeasonProgress, the old
// BWMClients code paths, etc) keep working
// without an immediate async refactor.
//
// Reads return whatever ClientsProvider last cached. Writes update the
// cache + fire-and-forget bulk-upsert to /api/data/clients.

import { apiFetch } from './apiFetch';
import { getClientsCache, setClientsCache } from './clientStore';

// Kept exported because some downstream code referenced it.
export const DATA_VERSION = 12;

// True if this client's engagement was active during `year` — i.e., service
// had started by Dec 31 of that year. A client signed in 2026 with a
// startDate of 2027-04-15 is NOT active in 2026 and contributes $0 to
// 2026 revenue rollups.
//
// Treats missing/invalid startDate as "active" so legacy records without
// dates don't disappear.
export function isClientActiveInYear(client, year) {
  if (!client) return false;
  if (!client.startDate) return true;
  const startYear = new Date(client.startDate).getFullYear();
  if (isNaN(startYear)) return true;
  return startYear <= year;
}

// How many months this client was active during `year`. Driven by
// startDate (narrows from Jan if started mid-year) and churnedDate
// (narrows from Dec if churned mid-year). Returns 0 if they weren't
// active at all that year.
//
// Examples for year=2026:
//   startDate=2025-06-01, churnedDate=null    → 12 (full year)
//   startDate=null, churnedDate=2026-04-27    → 4  (Jan–Apr)
//   startDate=2026-03-15, churnedDate=null    → 10 (Mar–Dec)
//   startDate=2026-03-15, churnedDate=2026-08-01 → 6 (Mar–Aug)
//   startDate=null, churnedDate=2025-09-01    → 0  (already gone)
//   startDate=2027-01-15, churnedDate=null    → 0  (not yet started)
export function computeMonthsActiveInYear(client, year) {
  if (!client) return 0;
  let firstActiveMonth = 0;   // Jan
  let lastActiveMonth = 11;   // Dec

  if (client.startDate) {
    const start = new Date(client.startDate);
    if (!isNaN(start.getTime())) {
      if (start.getFullYear() > year) return 0;
      if (start.getFullYear() === year) firstActiveMonth = start.getMonth();
    }
  }

  if (client.churnedDate) {
    const churn = new Date(client.churnedDate);
    if (!isNaN(churn.getTime())) {
      if (churn.getFullYear() < year) return 0;
      if (churn.getFullYear() === year) lastActiveMonth = churn.getMonth();
    }
  }

  return Math.max(0, lastActiveMonth - firstActiveMonth + 1);
}

// True if this client contributed revenue for the given month index (0-11)
// of the given year. Used to know which projected months to include vs
// zero out for churned clients.
export function isClientMonthActive(client, year, monthIdx) {
  if (!client) return false;
  if (client.startDate) {
    const start = new Date(client.startDate);
    if (!isNaN(start.getTime())) {
      if (start.getFullYear() > year) return false;
      if (start.getFullYear() === year && start.getMonth() > monthIdx) return false;
    }
  }
  if (client.churnedDate) {
    const churn = new Date(client.churnedDate);
    if (!isNaN(churn.getTime())) {
      if (churn.getFullYear() < year) return false;
      if (churn.getFullYear() === year && churn.getMonth() < monthIdx) return false;
    }
  }
  return true;
}

// Resolve the monthly rate in effect for a specific (year, monthIdx)
// by walking the client's priceHistory. Each priceHistory entry is
// { effectiveDate: 'YYYY-MM-DD', monthlyRevenue: number } and is the
// rate that took effect on that date and ran until the next entry (or
// forever). For a given month, we use the most recent entry whose
// effectiveDate is on or before the LAST DAY of that month.
//
// If no priceHistory entry applies (e.g., the client started before any
// recorded change), fall back to client.monthlyRevenue. If priceHistory
// is empty, this is a no-op and we just return monthlyRevenue.
export function getMonthlyRateForMonth(client, year, monthIdx) {
  const fallback = Number(client?.monthlyRevenue) || 0;
  const ph = Array.isArray(client?.priceHistory) ? client.priceHistory : [];
  if (ph.length === 0) return fallback;

  // Last day of (year, monthIdx) in YYYY-MM-DD form for string comparison.
  // Date(year, monthIdx + 1, 0) yields the last day of month `monthIdx`.
  const lastDay = new Date(year, monthIdx + 1, 0);
  const lastDayStr = `${lastDay.getFullYear()}-${String(lastDay.getMonth() + 1).padStart(2, '0')}-${String(lastDay.getDate()).padStart(2, '0')}`;

  let bestEffective = null;
  let bestRate = fallback;
  for (const entry of ph) {
    if (!entry?.effectiveDate) continue;
    if (entry.effectiveDate > lastDayStr) continue; // hasn't taken effect yet
    if (!bestEffective || entry.effectiveDate > bestEffective) {
      bestEffective = entry.effectiveDate;
      bestRate = Number(entry.monthlyRevenue) || 0;
    }
  }
  return bestRate;
}

// Return the revenue components this client contributes in `year`,
// broken down by type so consumers can roll up by division/category.
//
// Two pricing models, treated correctly:
//   Monthly recurring (BWM Guided/Total, BTS bookkeeping):
//     base = sum of (rate-in-effect × 1) per active month.
//     Without priceHistory, this collapses to monthlyRevenue × monthsActive.
//     With priceHistory, each month's rate is the latest effectiveDate ≤
//     end-of-month — supports mid-year price changes ($250→$750 May 1
//     yields $1,000 + $6,000 = $7,000 instead of $9,000).
//   One-time annual fee (BTS Tax Prep, BWM Tax Plan):
//     base = annualRevenue (full amount, NOT pro-rated by start date).
//     The fee is for the entire tax year regardless of when the client
//     signed. Katy joining May 1 still pays $1,500 for her 2026 return.
//     Mid-year churn pro-rates because the work doesn't get delivered:
//     base = annualRevenue × (monthsActive / 12) ONLY if churned mid-year.
//   BWM extras (investmentFeeAnnual): pro-rated by months active.
//   BWM extras (taxPlanFee): NOT pro-rated — same one-time logic as above.
//
// Returns all-zero components if the client wasn't active that year.
export function getClientRevenueComponents(client, year) {
  if (!client || !isClientActiveInYear(client, year)) {
    return { base: 0, investmentFee: 0, taxPlanFee: 0, total: 0, monthsActive: 0 };
  }
  const monthsActive = computeMonthsActiveInYear(client, year);
  if (monthsActive === 0) {
    return { base: 0, investmentFee: 0, taxPlanFee: 0, total: 0, monthsActive: 0 };
  }

  // Detect mid-year churn: client churned during `year` (so monthsActive < 12
  // because of churn, not because of late start). Mid-year churn DOES
  // pro-rate one-time fees because the engagement was cut short.
  const churnedMidYear = client.status === 'Churned'
    && client.churnedYear === year
    && monthsActive < 12;

  let base = 0;
  const ph = Array.isArray(client.priceHistory) ? client.priceHistory : [];
  if ((client.monthlyRevenue || 0) > 0 || ph.length > 0) {
    // Monthly recurring. Walk each month and sum the rate in effect.
    // Without priceHistory this is identical to monthlyRevenue × monthsActive.
    // With priceHistory, mid-year rate changes get computed correctly.
    for (let m = 0; m < 12; m++) {
      if (!isClientMonthActive(client, year, m)) continue;
      base += getMonthlyRateForMonth(client, year, m);
    }
  } else if ((client.annualRevenue || 0) > 0) {
    // One-time annual fee (Tax Prep, Tax Plan). Charge the FULL annual
    // amount unless the client churned mid-year (in which case some
    // portion of the work wasn't delivered).
    base = churnedMidYear
      ? client.annualRevenue * (monthsActive / 12)
      : client.annualRevenue;
  }

  const investmentFee = (client.investmentFeeAnnual || 0) * (monthsActive / 12);
  // taxPlanFee is a one-time annual extra (e.g., BWM client paying for a
  // tax plan in addition to their monthly fee). Same logic as base above:
  // full amount unless the engagement was cut short by mid-year churn.
  const taxPlanFee = churnedMidYear
    ? (client.taxPlanFee || 0) * (monthsActive / 12)
    : (client.taxPlanFee || 0);

  // For BWM the displayed total includes extras. For BTS/BIA it's just base.
  const total = client.division === 'bwm' ? base + investmentFee + taxPlanFee : base;

  return { base, investmentFee, taxPlanFee, total, monthsActive };
}

// Convenience wrapper for callers that just want a single number.
export function getClientRevenueForYear(client, year) {
  return getClientRevenueComponents(client, year).total;
}

export function loadClients() {
  return getClientsCache();
}

// Save (sync API surface) — updates cache immediately, persists to API
// in the background. Last-write-wins on the server.
export function saveClients(clients) {
  if (!Array.isArray(clients)) return;
  setClientsCache(clients);
  // Fire-and-forget; errors are logged so they're visible in DevTools.
  apiFetch('/api/data/clients/bulk-upsert', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(clients),
  }).then(async (res) => {
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      console.error('[saveClients] bulk-upsert failed:', data);
    }
  }).catch((err) => console.error('[saveClients] network error:', err));
}
